import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import React, { FC } from 'react';

export interface FieldWrapperProps {
	linkedInputId: string;
	label: string | React.JSX.Element;
	description?: string | React.JSX.Element;
	hasError?: boolean;
	helperText?: string | false;
	children: React.ReactNode;
}

/**
 * A wrapper for individual form elements that provides error handling via the `hasError` and `helperText` props.
 * Note, the `linkedInputId` parameter must be the same as the corresponding form input `id` to connect them in an accessible manner.
 *
 * @example
 * <FieldWrapper
 *   label={label}
 *   description={description}
 *   linkedInputId={id}
 *   hasError={hasError}
 *   helperText={errorText}
 * >
 *   <MuiSelect
 *     inputProps={{ id, name }}
 *     labelId={`${id}-label`}
 *     aria-describedby={`${id}-description`}
 *     value={value ?? ''}
 *     color='secondary'
 *     fullWidth
 *     onChange={onChange}
 *     {...rest}
 *   >
 *   ... contents
 *   </MuiSelect>
 * </FieldWrapper>
 */

const FieldWrapper: FC<FieldWrapperProps> = ({ linkedInputId, label, description, hasError, helperText, children }) => {
	return (
		<FormControl error={hasError} fullWidth>
			<Box
				component='label'
				id={`${linkedInputId ?? ''}-label`}
				htmlFor={linkedInputId}
				sx={{ fontWeight: 600, mb: description ? 0 : '1rem', color: '#000', fontSize: '18px', lineHeight: '24px' }}
			>
				{label}
			</Box>
			{description && (
				<Typography id={`${linkedInputId ?? ''}-description`} sx={{ mb: '1rem' }}>
					{description}
				</Typography>
			)}

			<Box>{children}</Box>

			{hasError && helperText && (
				<Box>
					<FormHelperText
						id={`${linkedInputId ?? ''}-helper-text`}
						sx={{ mx: 0, mt: '8px', fontSize: '16px', lineHeight: '24px' }}
					>
						{helperText}
					</FormHelperText>
				</Box>
			)}
		</FormControl>
	);
};

export default FieldWrapper;
